import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { AreaSelect } from "@/components/symin/AreaSelect";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { StackSimple, ArrowSquareOut, ArrowRight, Waveform, Mountains, Planet, Path, ChartBar, Warning, FileArrowDown, ArrowCounterClockwise, X, Plus } from "@phosphor-icons/react";

const SENSOR_ICON = { composite: Planet, dem: Mountains, sar: Waveform, structural: Path };

function LayerThumb({ pid, aid, layerKey, big }) {
  const [uri, setUri] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    let cancelled = false;
    setState("loading"); setUri(null);
    api.get(`/projects/${pid}/areas/${aid}/map/${layerKey}?inline=1`)
      .then((r) => { if (!cancelled) { setUri(r.data.uri); setState("ok"); } })
      .catch(() => { if (!cancelled) setState("err"); });
    return () => { cancelled = true; };
  }, [pid, aid, layerKey]);
  return (
    <div className={`relative w-full ${big ? "aspect-[16/10]" : "aspect-[4/3]"} rounded-md overflow-hidden bg-[#0B1420] border border-border`} data-testid={`fusion-thumb-${layerKey}`}>
      {state === "ok" && uri && <img src={uri} alt={layerKey} className="absolute inset-0 w-full h-full object-cover" />}
      {state === "loading" && <div className="absolute inset-0 grid place-items-center text-[10px] text-muted-foreground animate-pulse">rendering…</div>}
      {state === "err" && <div className="absolute inset-0 grid place-items-center text-[10px] text-muted-foreground">unavailable</div>}
    </div>
  );
}

export default function SensorFusion() {
  const { id } = useParams();
  const nav = useNavigate();
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState(null);
  const [fusion, setFusion] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [status, setStatus] = useState("idle");
  const [spec, setSpec] = useState(75);
  const [preview, setPreview] = useState(null);
  const [touched, setTouched] = useState(false);
  const [savingW, setSavingW] = useState(false);
  const [allAreas, setAllAreas] = useState(false);
  const [reportWeights, setReportWeights] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [presetAdding, setPresetAdding] = useState(false);
  const dbRef = useRef(null);

  const saveWeights = async () => {
    setSavingW(true);
    try {
      const q = `spectral=${spec}&structural=${100 - spec}` + (allAreas ? "" : `&area_id=${area}`);
      await api.put(`/projects/${id}/report-fusion-weights?${q}`);
      setReportWeights({ spectral: spec, structural: 100 - spec, scope: allAreas ? "project" : "area" });
      toast.success(`Saved - the Master PDF ${allAreas ? "(all areas)" : "(this area)"} will bake in spectral ${spec}% / structural ${100 - spec}%.`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingW(false); }
  };

  const resetWeights = async () => {
    if (!area) return;
    try {
      await api.delete(`/projects/${id}/report-fusion-weights?area_id=${area}`);
      const r = await api.get(`/projects/${id}/areas/${area}/fusion`);
      setFusion(r.data);
      toast.success("Per-area override cleared - this area now uses the project default.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const generatePdf = async () => {
    if (!area) return;
    setPdfBusy(true);
    try {
      const q = `spectral=${spec}&structural=${100 - spec}` + (allAreas ? "" : `&area_id=${area}`);
      await api.put(`/projects/${id}/report-fusion-weights?${q}`);
      setReportWeights({ spectral: spec, structural: 100 - spec, scope: allAreas ? "project" : "area" });
      const res = await api.get(`/projects/${id}/master-report/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "SYMIN_Master_Report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Master PDF generated - baked at spectral ${spec}% / structural ${100 - spec}% (${allAreas ? "all areas" : "this area"}).`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPdfBusy(false); }
  };

  const applyPreset = async (p) => {
    if (!area) return;
    setSpec(p.spectral); setTouched(true);
    try {
      const q = `spectral=${p.spectral}&structural=${p.structural}` + (allAreas ? "" : `&area_id=${area}`);
      await api.put(`/projects/${id}/report-fusion-weights?${q}`);
      setReportWeights({ spectral: p.spectral, structural: p.structural, scope: allAreas ? "project" : "area" });
      toast.success(`Applied "${p.name}" - spectral ${p.spectral}% / structural ${p.structural}% (${allAreas ? "all areas" : "this area"}).`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) return;
    try {
      const r = await api.post(`/projects/${id}/fusion-presets`, { name, spectral: spec, structural: 100 - spec });
      setPresets(r.data.presets || []); setPresetName(""); setPresetAdding(false);
      toast.success(`Preset "${name}" saved.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const deletePreset = async (name) => {
    try {
      const r = await api.delete(`/projects/${id}/fusion-presets?name=${encodeURIComponent(name)}`);
      setPresets(r.data.presets || []);
      toast.success(`Preset "${name}" removed.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (!fusion) return;
    const sw = (fusion.inputs || []).find((i) => i.key === "composite")?.weight;
    setSpec(sw != null ? sw : 75);
    setTouched(false); setPreview(null);
    setReportWeights(fusion.report_weights || null);
    setPresets(fusion.presets || []); setPresetAdding(false); setPresetName("");
  }, [fusion]);

  useEffect(() => {
    if (!touched || !area) return;
    if (dbRef.current) clearTimeout(dbRef.current);
    dbRef.current = setTimeout(() => {
      api.get(`/projects/${id}/areas/${area}/fusion-preview?spectral=${spec}&structural=${100 - spec}`)
        .then((r) => setPreview(r.data)).catch(() => {});
    }, 220);
    return () => dbRef.current && clearTimeout(dbRef.current);
  }, [spec, touched, area, id]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/projects/${id}/map-data`);
        setAreas(r.data.areas || []);
        const first = (r.data.areas || []).find((a) => a.status === "complete") || (r.data.areas || [])[0];
        if (first) setArea(first.area_id);
      } catch (e) { toast.error(apiError(e)); }
      try {
        const rr = await api.get(`/projects/${id}/prospectivity-ranking`);
        setRanking(rr.data);
      } catch (e) { /* non-fatal */ }
    })();
  }, [id]);

  useEffect(() => {
    if (!area) return;
    let cancelled = false;
    setStatus("loading"); setFusion(null);
    api.get(`/projects/${id}/areas/${area}/fusion`)
      .then((r) => { if (!cancelled) { setFusion(r.data); setStatus("ready"); } })
      .catch((e) => { if (!cancelled) { setStatus("pending"); if (e?.response?.status !== 400) toast.error(apiError(e)); } });
    return () => { cancelled = true; };
  }, [id, area]);

  const maxKm2 = Math.max(0.0001, ...((ranking?.minerals || []).map((m) => m.high_km2 || 0)));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="fusion" />
      <div className="ml-[64px] flex flex-col min-h-screen">
        <header className="h-[52px] shrink-0 bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5 sticky top-0 z-20">
          <StackSimple size={18} weight="fill" style={{ color: "#F59E0B" }} className="shrink-0" />
          <div className="min-w-0">
            <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate" data-testid="fusion-title">Multi-Sensor Fusion</h1>
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-muted-foreground -mt-0.5 hidden sm:block">SAR · DEM · Multispectral → Prospectivity</div>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <AreaSelect areas={areas} value={area} onChange={setArea} accent="#F59E0B" testid="fusion-area-select" />
            <Button size="sm" className="h-8 shrink-0 font-semibold" onClick={generatePdf} disabled={pdfBusy || !area}
              data-testid="fusion-generate-pdf" style={{ background: "#C20511", color: "#FFFFFF" }}>
              <FileArrowDown size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">{pdfBusy ? "Generating…" : "Generate Master PDF"}</span>
            </Button>
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="fusion-back">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 p-3 sm:p-5 space-y-5">
          {status === "pending" && (
            <div className="rounded-lg border border-border bg-card p-8 grid place-items-center" data-testid="fusion-pending">
              <div className="flex flex-col items-center gap-2 text-muted-foreground max-w-sm text-center">
                <Warning size={26} style={{ color: "#FBBF24" }} />
                <span className="text-sm">This licence area has not been processed yet. Process it in Data &amp; Setup to run the fusion model.</span>
              </div>
            </div>
          )}

          {status === "ready" && fusion && (
            <section className="rounded-lg border border-border bg-card overflow-hidden animate-fade-in" data-testid="fusion-pipeline">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                <h2 className="font-display font-semibold text-sm">Evidence Fusion Pipeline · {fusion.name}</h2>
                <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground hidden sm:inline">{fusion.evidence?.streams_available}/{fusion.evidence?.streams_expected} streams live</span>
              </div>

              <div className="p-4 grid grid-cols-1 xl:grid-cols-[1fr_auto_1.15fr] gap-4 items-center">
                {/* inputs */}
                <div className="grid grid-cols-2 gap-3" data-testid="fusion-inputs">
                  {(fusion.inputs || []).map((inp) => {
                    const Icon = SENSOR_ICON[inp.key] || Planet;
                    return (
                      <div key={inp.key} className={`rounded-md border p-2.5 transition-all duration-200 hover:-translate-y-0.5 ${inp.available ? "border-border bg-[#0B1420] hover:border-[#F59E0B]/50" : "border-dashed border-border/60 bg-transparent opacity-60"}`} data-testid={`fusion-input-${inp.key}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Icon size={14} style={{ color: "#38BDF8" }} />
                          <span className="text-[11px] font-semibold truncate">{inp.label}</span>
                          {inp.weight != null && <span className="ml-auto text-[9px] font-mono px-1 rounded bg-[#16243D] text-[#C20511]">{inp.weight}%</span>}
                        </div>
                        {inp.available ? <LayerThumb pid={id} aid={area} layerKey={inp.key} /> : <div className="aspect-[4/3] rounded-md grid place-items-center text-[9px] text-muted-foreground border border-dashed border-border/60">not available</div>}
                        <div className="mt-1.5 text-[9px] text-muted-foreground leading-tight">
                          <div className="text-[#94A3B8] font-mono uppercase tracking-wide">{inp.sensor}</div>
                          {inp.role}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* merge arrow */}
                <div className="hidden xl:flex flex-col items-center gap-1 px-1 text-[#F59E0B]">
                  <ArrowRight size={26} weight="bold" />
                  <span className="text-[8px] font-mono uppercase tracking-widest text-muted-foreground rotate-90 xl:rotate-0">fuse</span>
                </div>

                {/* fused output */}
                <div className="rounded-md border border-[#C20511]/40 bg-[#0B1420] p-3" data-testid="fusion-output">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-display font-semibold text-[#C20511]">{fusion.fused?.label}</span>
                    {fusion.confidence?.mean != null && (
                      <span className="ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#16243D] text-[#38BDF8]">
                        confidence {fusion.confidence.mean}% · {fusion.confidence.class || "-"}
                      </span>
                    )}
                  </div>
                  {touched && preview?.uri ? (
                    <div className="relative w-full aspect-[16/10] rounded-md overflow-hidden bg-[#0B1420] border border-[#C20511]/30" data-testid="fusion-preview-img">
                      <img src={preview.uri} alt="live re-blend" className="absolute inset-0 w-full h-full object-cover" />
                      <span className="absolute top-1.5 left-1.5 text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0B1420]/80 text-[#C20511] border border-[#C20511]/40">live re-blend</span>
                    </div>
                  ) : (
                    <LayerThumb pid={id} aid={area} layerKey={fusion.fused?.key || "prospectivity"} big />
                  )}
                  {/* live evidence-weight slider */}
                  <div className="mt-3" data-testid="fusion-weight">
                    <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-wider mb-1.5">
                      <span className="text-[#38BDF8]">Spectral {spec}%</span>
                      <span className="text-muted-foreground">Evidence weighting</span>
                      <span className="text-[#A855F7]">Structural {100 - spec}%</span>
                    </div>
                    <Slider data-testid="fusion-weight-slider" value={[spec]} min={0} max={100} step={5}
                      onValueChange={(v) => { setSpec(v[0]); setTouched(true); }} />
                    <div className="mt-2 flex items-center gap-2">
                      <div className="text-[10px] text-muted-foreground leading-snug flex-1">
                        {touched ? "Live preview - drag to re-blend the spectral vs. structural evidence." : (fusion.fused?.method || "Multi-sensor evidence fusion")}
                      </div>
                      <label className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer select-none shrink-0" data-testid="fusion-save-scope">
                        <input type="checkbox" checked={allAreas} onChange={(e) => setAllAreas(e.target.checked)} className="accent-[#C20511] h-3 w-3" /> All areas
                      </label>
                      <Button size="sm" variant="outline" data-testid="fusion-save-weights" onClick={saveWeights} disabled={savingW}
                        className="h-7 shrink-0 border-[#C20511]/50 text-[#C20511] hover:bg-[#C20511]/10 text-[11px]">
                        {savingW ? "Saving…" : "Save to report"}
                      </Button>
                    </div>
                    {reportWeights && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span data-testid="fusion-weight-badge" className="inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-[#16243D] text-[#C20511] border border-[#C20511]/30">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#C20511]" />
                          Report baked at {reportWeights.spectral}% / {reportWeights.structural}% · {reportWeights.scope === "area" ? "this area" : "all areas"}
                        </span>
                        {reportWeights.scope === "area" && (
                          <button onClick={resetWeights} data-testid="fusion-weight-reset"
                            className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground">
                            <ArrowCounterClockwise size={12} /> Reset to project default
                          </button>
                        )}
                      </div>
                    )}
                    {/* weighting presets */}
                    <div className="mt-3 pt-2.5 border-t border-border/60" data-testid="fusion-presets">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="text-[8px] font-mono uppercase tracking-wider text-muted-foreground">Presets</span>
                        <span className="text-[9px] text-muted-foreground/70">one-click apply · {allAreas ? "all areas" : "this area"}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {presets.map((p) => (
                          <span key={p.name} className="inline-flex items-center rounded-full border border-[#C20511]/40 bg-[#0B1420] overflow-hidden" data-testid={`fusion-preset-${p.name}`}>
                            <button onClick={() => applyPreset(p)} data-testid={`fusion-preset-apply-${p.name}`}
                              className="pl-2.5 pr-1.5 py-1 text-[10px] text-[#C20511] hover:bg-[#C20511]/10 transition-colors">
                              {p.name} <span className="text-muted-foreground">· {p.spectral}/{p.structural}</span>
                            </button>
                            <button onClick={() => deletePreset(p.name)} data-testid={`fusion-preset-del-${p.name}`}
                              className="px-1.5 py-1 text-muted-foreground hover:text-[#F43F5E] border-l border-[#C20511]/20 transition-colors" title="Remove preset">
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                        {presetAdding ? (
                          <span className="inline-flex items-center gap-1">
                            <input autoFocus value={presetName} onChange={(e) => setPresetName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") savePreset(); if (e.key === "Escape") { setPresetAdding(false); setPresetName(""); } }}
                              placeholder={`Name e.g. Structure-led ${spec}/${100 - spec}`} maxLength={40}
                              data-testid="fusion-preset-name"
                              className="h-7 w-44 rounded-md bg-[#0B1420] border border-[#C20511]/40 px-2 text-[11px] text-foreground outline-none focus:border-[#C20511]" />
                            <Button size="sm" variant="outline" onClick={savePreset} disabled={!presetName.trim()} data-testid="fusion-preset-save"
                              className="h-7 shrink-0 border-[#C20511]/50 text-[#C20511] hover:bg-[#C20511]/10 text-[11px]">Save</Button>
                            <button onClick={() => { setPresetAdding(false); setPresetName(""); }} className="text-[10px] text-muted-foreground hover:text-foreground px-1">Cancel</button>
                          </span>
                        ) : (
                          <button onClick={() => setPresetAdding(true)} data-testid="fusion-preset-add"
                            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-1 text-[10px] text-muted-foreground hover:text-foreground hover:border-[#C20511]/50 transition-colors">
                            <Plus size={10} /> Save current {spec}/{100 - spec}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* evidence chips */}
              <div className="px-4 pb-4 flex flex-wrap gap-2" data-testid="fusion-evidence">
                <Chip label="Sensors" value={(fusion.evidence?.sensors || []).join(" · ")} />
                <Chip label="Lineaments" value={fusion.evidence?.n_lineaments ?? "-"} />
                <Chip label="Imagery coverage" value={fusion.evidence?.coverage_pct != null ? `${fusion.evidence.coverage_pct}%` : "-"} />
                <Chip label="Evidence factor" value={fusion.evidence?.evidence_factor ?? "-"} />
              </div>
            </section>
          )}

          {/* Automated mineral prospectivity ranking */}
          <section className="rounded-lg border border-border bg-card overflow-hidden animate-fade-in" data-testid="fusion-ranking">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <ChartBar size={16} weight="fill" style={{ color: "#C20511" }} />
              <h2 className="font-display font-semibold text-sm">Automated Mineral Prospectivity Ranking</h2>
              {ranking?.areas_assessed != null && <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground hidden sm:inline">{ranking.areas_assessed} areas assessed</span>}
            </div>
            {ranking?.has_minerals ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-[#0B1420]">
                      <th className="text-left font-medium px-3 py-2">#</th>
                      <th className="text-left font-medium px-3 py-2">Mineral</th>
                      <th className="text-left font-medium px-3 py-2 hidden sm:table-cell">Group</th>
                      <th className="text-right font-medium px-3 py-2">Areas</th>
                      <th className="px-3 py-2 min-w-[160px]">High-zone footprint</th>
                      <th className="text-right font-medium px-3 py-2 hidden md:table-cell">Peak %</th>
                      <th className="text-right font-medium px-3 py-2 hidden md:table-cell">P1</th>
                      <th className="text-right font-medium px-3 py-2 hidden lg:table-cell">Indic. HM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.minerals.map((m) => (
                      <tr key={m.mineral} className="border-t border-border hover:bg-white/[0.02]" data-testid={`ranking-row-${m.rank}`}>
                        <td className="px-3 py-2">
                          <span className={`inline-grid place-items-center h-6 w-6 rounded-full text-[11px] font-bold ${m.rank === 1 ? "bg-[#C20511] text-white" : "bg-[#16243D] text-slate-200"}`}>{m.rank}</span>
                        </td>
                        <td className="px-3 py-2 font-semibold">{m.mineral}</td>
                        <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">{m.group}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{m.areas}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-[#16243D] overflow-hidden min-w-[70px]">
                              <div className="h-full rounded-full" style={{ width: `${Math.max(4, ((m.high_km2 || 0) / maxKm2) * 100)}%`, background: "linear-gradient(90deg,#F59E0B,#C20511)" }} />
                            </div>
                            <span className="text-[11px] tabular-nums text-muted-foreground w-16 text-right">{m.high_km2} km²</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell">{m.best_pct}%</td>
                        <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell">{m.targets}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground hidden lg:table-cell">{m.hm_mt_high >= 0.001 ? `${m.hm_mt_low}–${m.hm_mt_high} Mt` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-3 py-2.5 text-[10px] text-muted-foreground border-t border-border">
                  Ranked by aggregate High-priority target-zone footprint across all assessed areas. Conceptual, model-derived estimates - not a Mineral Resource.
                </div>
              </div>
            ) : (
              <div className="p-8 grid place-items-center text-center" data-testid="fusion-ranking-empty">
                <div className="max-w-md text-sm text-muted-foreground">
                  No multi-mineral analysis found yet. Run <span className="text-foreground font-medium">Multi-Mineral Analysis</span> on one or more areas (Data &amp; Setup) to generate the automated prospectivity ranking.
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value }) {
  return (
    <div className="rounded-md border border-border bg-[#0B1420] px-2.5 py-1.5">
      <div className="text-[8px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-[11px] font-semibold text-foreground truncate max-w-[180px]">{String(value)}</div>
    </div>
  );
}
